import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { EMOJI_LIST, type EmojiEntry } from './emojiData';

interface EmojiPickerPopoverProps {
  anchorRef: React.RefObject<HTMLButtonElement>;
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

const EMOJI_GRID_SIZE = 28;
const GRID_GAP = 1;
/** Matches PopoverWrapper vertical padding (top + bottom). */
const POPOVER_VERTICAL_PADDING = 4;
/** Approx. outer height for flip-above/below positioning (grid + padding + borders). */
const POPOVER_HEIGHT = EMOJI_GRID_SIZE + POPOVER_VERTICAL_PADDING * 2 + 2;
const ANCHOR_GAP = 4;

const PopoverWrapper = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  z-index: 1000;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: max-content;
  max-width: calc(100vw - ${theme.dime.spacing.m});
  padding: ${POPOVER_VERTICAL_PADDING}px ${theme.dime.spacing.s};
  box-sizing: border-box;
  border-radius: ${theme.dime.radius.m};
  border: 1px solid ${theme.dime.colors.border.static};
  background: ${theme.dime.colors.surface.primary};
  box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.08);
`;

const EmojiGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns }) => $columns},
    ${EMOJI_GRID_SIZE}px
  );
  gap: ${GRID_GAP}px;
`;

const EmojiButton = styled.button`
  width: ${EMOJI_GRID_SIZE}px;
  height: ${EMOJI_GRID_SIZE}px;
  padding: 0;
  border: none;
  border-radius: ${theme.dime.radius.s};
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.dime.colors.surface.primaryHover};
  }
`;

export const EmojiPickerPopover: React.FC<EmojiPickerPopoverProps> = ({
  anchorRef,
  onSelect,
  onClose
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) {
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const top =
      spaceAbove >= POPOVER_HEIGHT + ANCHOR_GAP
        ? rect.top - POPOVER_HEIGHT - ANCHOR_GAP
        : rect.bottom + ANCHOR_GAP;
    const left = Math.max(0, rect.left);
    setPosition({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  const handleEmojiClick = (item: EmojiEntry) => {
    onSelect(item.emoji);
  };

  return createPortal(
    <PopoverWrapper ref={wrapperRef} $top={position.top} $left={position.left}>
      <EmojiGrid $columns={EMOJI_LIST.length}>
        {EMOJI_LIST.map((item) => (
          <EmojiButton
            key={item.name}
            type="button"
            onClick={() => handleEmojiClick(item)}
            title={item.name}
          >
            {item.emoji}
          </EmojiButton>
        ))}
      </EmojiGrid>
    </PopoverWrapper>,
    document.body
  );
};
